/**
 * Session Renewal Service
 *
 * Monitors session expiration and triggers renewal or inactivity warnings.
 * Works with sessionActivityService to determine if user should be prompted.
 */

// Dynamic import to avoid circular dependency - only loaded when needed
async function loadStore() {
  return (await import('./store.js')).store
}

// Dynamic import to avoid circular dependency with auth-service
async function loadAuthService() {
  return (await import('./auth-service.js')).AuthService
}

// Dynamic import to avoid circular dependency with sessionActivityService
async function loadSessionActivityService() {
  return (await import('./sessionActivityService.js')).sessionActivityService
}

export class SessionRenewalService {
    constructor() {
       this.checkTimer = null
       this.tokenExpiresAt = null
       this.nextCheckTime = null
       this.lastRenewalAttemptTime = null // Track last renewal to prevent rapid renewal loops
       // Tracks the customerId whose session config has already been merged
       // into this.config, so repeated getCurrentUser() side-effect calls
       // don't refetch /config/session/{customerId} on every login-page tick.
       this._configInitializedFor = null
       this.config = {
          sessionDurationMinutes: 60,
          renewBeforeExpiryMinutes: 1,
          maxRenewalRetries: 3,
          retryDelayMs: 5000,
          activityDebounceMs: 10000,
          checkIntervalMs: 30000, // Check every 30 seconds
          enableAutoRenewal: true,
          allowImpersonation: true,
          maxImpersonationDurationMinutes: 60
       }
       this.listeners = {
          'renewal-needed': [],
          'renewal-success': [],
          'renewal-failed': [],
          'show-inactivity-prompt': [],
          'session-expired': []
       }
       this.retryCount = 0
       this.skipNextInitialCheck = false
    }

   /**
    * Initialize service with token expiration time
    * @param {Date|string} tokenExpiresAt - When the session token expires
    */
   initialize(tokenExpiresAt) {
      if (typeof tokenExpiresAt === 'string') {
         this.tokenExpiresAt = new Date(tokenExpiresAt)
      } else {
         this.tokenExpiresAt = tokenExpiresAt
      }
      
      console.log('🔄 Session renewal initialized:', {
         expiresAt: this.tokenExpiresAt.toISOString(),
         minutesUntilExpiry: Math.floor((this.tokenExpiresAt - new Date()) / 60000)
      })
      
      this.startMonitoring()
   }

   /**
    * Initialize session config from the merged SessionSecurityConfig
    * carried by the /auth/me response (`sessionConfig`). The boot path
    * is the only producer of this shape — the public
    * /api/v1/config/session/{customerId} endpoint is the admin editor
    * surface and intentionally returns a narrowed view that does NOT
    * include the renewal-loop tuning fields this service depends on,
    * so we don't fall back to it. If `prefetchedConfig` is missing the
    * service runs on the constructor defaults at the top of this file.
    *
    * @param {string} customerId - Customer ID
    * @param {Object} [prefetchedConfig] - Pre-merged
    *     SessionSecurityConfig from /auth/me.
    * @returns {Object|null} Updated config or null when no payload was
    *     supplied (constructor defaults remain in effect).
    */
   initializeSessionConfig(customerId, prefetchedConfig) {
      // Skip if this customer's config has already been merged.
      if (customerId && this._configInitializedFor === customerId) {
         return this.config
      }

      if (!prefetchedConfig) {
         console.warn('🔄 No prefetched session config supplied — running on defaults:', this.config)
         return null
      }

      const renewalConfig = {
         sessionDurationMinutes: prefetchedConfig.sessionDurationMinutes,
         renewBeforeExpiryMinutes: prefetchedConfig.renewBeforeExpiryMinutes,
         maxRenewalRetries: prefetchedConfig.maxRenewalRetries,
         retryDelayMs: prefetchedConfig.retryDelayMs,
         activityDebounceMs: prefetchedConfig.activityDebounceMs,
         checkIntervalMs: prefetchedConfig.checkIntervalMs,
         enableAutoRenewal: prefetchedConfig.enableAutoRenewal,
         allowImpersonation: prefetchedConfig.allowImpersonation,
         maxImpersonationDurationMinutes: prefetchedConfig.maxImpersonationDurationMinutes,
      }

      this.updateConfig(renewalConfig)
      this._configInitializedFor = customerId
      console.log('🔄 Session renewal config applied from prefetched payload:', this.config)
      return renewalConfig
   }

   /**
    * Start monitoring for renewal window
    * Simple periodic checks every checkIntervalMs
    */
   startMonitoring() {
      if (this.checkTimer) {
         console.log('⚠️ Session renewal already monitoring')
         return
      }

      // Check immediately (unless flagged to skip)
      if (!this.skipNextInitialCheck) {
         this.checkRenewalWindow().catch(err => console.error('Error during initial renewal check:', err))
      } else {
         this.skipNextInitialCheck = false
         console.log('⏭️ Skipping initial renewal check (resuming after test)')
      }
      
      // Schedule periodic checks
      this.checkTimer = setInterval(() => {
         this.checkRenewalWindow().catch(err => console.error('Error during renewal check:', err))
      }, this.config.checkIntervalMs)
      
      console.log('✅ Session renewal monitoring started')
   }

   /**
    * Stop monitoring
    */
   stopMonitoring() {
      if (this.checkTimer) {
         clearTimeout(this.checkTimer)
         clearInterval(this.checkTimer)
         this.checkTimer = null
         console.log('✅ Session renewal monitoring stopped')
      }
   }

   /**
    * Check if we're in the renewal window and take appropriate action
    */
   async checkRenewalWindow() {
      if (!this.tokenExpiresAt) {
         console.warn('⚠️ No token expiration set, cannot check renewal window')
         return
      }

      const now = new Date()
      const timeUntilExpiry = this.tokenExpiresAt - now
      const renewalWindowMs = this.config.renewBeforeExpiryMinutes * 60 * 1000

      // Session already expired
      if (timeUntilExpiry <= 0) {
         console.log('⏰ Session has expired')
         this.stopMonitoring()
         
         // Verify user is still authenticated before emitting session-expired
         // This prevents false positives when viewing login page after logout
         const AuthService = await loadAuthService()
         const isAuthenticated = await AuthService.isAuthenticated()
         if (isAuthenticated) {
            console.log('✅ User still authenticated - emitting session-expired event')
            this.emit('session-expired')
         } else {
            console.log('🔓 User not authenticated - skipping session-expired event (user already logged out)')
         }
         return
      }

      // Not yet in renewal window
      if (timeUntilExpiry > renewalWindowMs) {
         // console.log('⏱️ Not yet in renewal window:', {
         //   minutesUntilRenewal: Math.floor((timeUntilExpiry - renewalWindowMs) / 60000)
         // })
         return
      }

      // In renewal window - check activity and access-session status
      console.log('🔔 Entered renewal window:', {
         minutesRemaining: Math.floor(timeUntilExpiry / 60000)
      })

      const store = await loadStore()
      const storeState = store.getState()

      // METALARK-719 §3: kind-aware renewal policy.
      //
      //   IMPERSONATION: never auto-renew. Audit-critical fixed TTL means
      //     the admin must explicitly extend the session (currently not
      //     supported — they need to end and restart). Inactivity prompt
      //     still fires to warn them.
      //
      //   WORKSPACE_ACCESS: auto-renew in lockstep with the primary auth_token.
      //     These are first-class-role sessions and shouldn't silently
      //     expire while the admin is actively working. Backend extends the
      //     wsa_ session's expiresAt as a side effect of the primary
      //     renew-session endpoint.
      //
      //   No access session: auto-renew as usual.
      //
      // Absent kind on older Redux state is treated as IMPERSONATION for
      // back-compat — matches QueryContextTypes.isImpersonating semantics.
      const accessSession = storeState.impersonation
      const sessionKind = accessSession?.expiresAt
         ? (accessSession.kind || 'IMPERSONATION')
         : null
      const isImpersonating = sessionKind === 'IMPERSONATION'

      // Lazy load sessionActivityService
      const sessionActivityService = await loadSessionActivityService()

      if (isImpersonating) {
         console.log('👤 Impersonation session - checking activity for prompt')
         // Still show inactivity prompt if inactive
         const thresholdMs = this.config.renewBeforeExpiryMinutes * 60 * 1000
         if (!sessionActivityService.isActiveWithinThreshold(thresholdMs)) {
            console.log('⚠️ Impersonation user inactive - showing prompt')
            this.emit('show-inactivity-prompt', { timeRemaining: timeUntilExpiry })
         } else {
            console.log('✅ Impersonation user active but no auto-renewal')
         }
         // Stop checking once we've entered renewal window for impersonation
         this.stopMonitoring()
         return
      }

      // Check if user was active within renewal window threshold
      const thresholdMs = this.config.renewBeforeExpiryMinutes * 60 * 1000
      const isActive = sessionActivityService.isActiveWithinThreshold(thresholdMs)
      
      if (isActive) {
         // User is active - renew silently
         console.log('✅ User active within threshold - auto-renewing session')
         this.renewToken()
      } else {
         // User is inactive - show prompt
         const inactiveTime = sessionActivityService.getTimeSinceLastActivity()
         console.log('⚠️ User inactive - showing inactivity prompt:', {
            inactiveForMinutes: Math.floor(inactiveTime / 60000),
            lastActivity: sessionActivityService.getFormattedTimeSinceActivity()
         })
         this.emit('show-inactivity-prompt', { timeRemaining: timeUntilExpiry })
      }
      
      // Stop checking once we've processed the renewal window
      this.stopMonitoring()
   }

   /**
    * Renew the session token
    * @param {number} retryCount - Current retry attempt (for internal use)
    */
   async renewToken(retryCount = 0) {
      // Rate limit: prevent renewal more than once every 2 minutes
      // This prevents cascading renewal attempts when expiry time gets close
      const MIN_RENEWAL_INTERVAL_MS = 120000; // 2 minutes
      const now = Date.now();
      
      if (this.lastRenewalAttemptTime && (now - this.lastRenewalAttemptTime) < MIN_RENEWAL_INTERVAL_MS) {
         const timeSinceLastAttempt = Math.round((now - this.lastRenewalAttemptTime) / 1000);
         const timeUntilNextAllowed = Math.round((MIN_RENEWAL_INTERVAL_MS - (now - this.lastRenewalAttemptTime)) / 1000);
         console.log(`⚠️ Renewal rate limited: ${timeSinceLastAttempt}s since last attempt, need to wait ${timeUntilNextAllowed}s more`);
         return; // Skip this renewal attempt
      }

      try {
         this.lastRenewalAttemptTime = now; // Track this renewal attempt
         this.emit('renewal-needed')
         console.log('🔄 Attempting session renewal...', retryCount > 0 ? `(retry ${retryCount})` : '')

         const response = await fetch('/api/v1/account/renew-session', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json'
            },
            credentials: 'include' // Include httpOnly cookie
         })

         if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Renewal failed with status ${response.status}: ${errorText}`)
         }

         const data = await response.json()
         
         // Update expiration time
         this.tokenExpiresAt = new Date(data.expiresAt)
         this.retryCount = 0 // Reset retry count on success
         
         console.log('✅ Session renewed successfully:', {
            newExpiresAt: this.tokenExpiresAt.toISOString(),
            minutesUntilExpiry: Math.floor((this.tokenExpiresAt - new Date()) / 60000)
         })

         // METALARK-719 §3: if the caller is in a WORKSPACE_ACCESS session,
         // the backend extended its expiresAt to match the new auth_token.
         // Mirror that into Redux so the navbar session timer (if any) +
         // any card showing remainingSeconds update without another
         // getCurrentSession roundtrip. IMPERSONATION sessions aren't
         // touched — backend leaves them alone, frontend leaves them alone.
         try {
            const store = await loadStore()
            const current = store.getState().impersonation
            if (
               current?.token
               && (current.kind || 'IMPERSONATION') === 'WORKSPACE_ACCESS'
               && data?.expiresAt
            ) {
               store.dispatch({
                  type: 'setImpersonation',
                  payload: {
                     ...current,
                     expiresAt: data.expiresAt,
                  },
               })
            }
         } catch (err) {
            console.warn('[SessionRenewal] Could not update workspace-access expiresAt in Redux:', err)
         }

         this.emit('renewal-success', data)

         // Restart monitoring with new expiration time
         this.stopMonitoring()
         this.startMonitoring()

      } catch (error) {
         console.error('❌ Token renewal failed:', error)

         if (retryCount < this.config.maxRenewalRetries) {
            // Retry after delay
            console.log(`⏳ Retrying renewal in ${this.config.retryDelayMs / 1000} seconds...`)
            setTimeout(() => {
               this.renewToken(retryCount + 1)
            }, this.config.retryDelayMs)
         } else {
            console.error('❌ Max retries reached, giving up')
            this.emit('renewal-failed', error)
         }
      }
   }

   /**
    * Subscribe to renewal events
    * @param {string} event - Event name
    * @param {Function} callback - Event handler
    * @returns {Function} Unsubscribe function
    */
   on(event, callback) {
      if (this.listeners[event]) {
         this.listeners[event].push(callback)
      } else {
         console.warn(`⚠️ Unknown event: ${event}`)
      }
      
      return () => {
         if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
         }
      }
   }

   /**
    * Emit an event to all listeners
    * @param {string} event - Event name
    * @param {*} data - Event data
    */
   emit(event, data) {
      if (this.listeners[event]) {
         this.listeners[event].forEach(callback => {
            try {
               callback(data)
            } catch (error) {
               console.error(`❌ Error in ${event} listener:`, error)
            }
         })
      }
   }

   /**
    * Update configuration
    * @param {Object} newConfig - Configuration overrides
    */
   updateConfig(newConfig) {
      this.config = { ...this.config, ...newConfig }
      console.log('⚙️ Session renewal config updated:', this.config)
   }

   /**
    * Get current status
    * @returns {Object} Status information
    */
   getStatus() {
      if (!this.tokenExpiresAt) {
         return {
            initialized: false,
            expiresAt: null,
            minutesRemaining: null,
            inRenewalWindow: false
         }
      }

      const now = new Date()
      const timeUntilExpiry = this.tokenExpiresAt - now
      const renewalWindowMs = this.config.renewBeforeExpiryMinutes * 60 * 1000
      
      return {
         initialized: true,
         expiresAt: this.tokenExpiresAt.toISOString(),
         minutesRemaining: Math.floor(timeUntilExpiry / 60000),
         secondsRemaining: Math.floor(timeUntilExpiry / 1000),
         inRenewalWindow: timeUntilExpiry <= renewalWindowMs && timeUntilExpiry > 0,
         renewalWindowMinutes: this.config.renewBeforeExpiryMinutes,
         expired: timeUntilExpiry <= 0
      }
   }

   /**
    * Force check renewal window (for testing)
    */
   forceCheck() {
      console.log('🔍 Force checking renewal window...')
      this.checkRenewalWindow()
   }

   /**
    * Reset renewal rate limiting (for testing)
    * Allows the next renewal attempt to proceed immediately
    */
   resetRenewalRateLimit() {
      this.lastRenewalAttemptTime = null
      console.log('🔄 Renewal rate limit reset - next renewal will proceed immediately')
   }

   /**
    * Cleanup
    */
   destroy() {
      this.stopMonitoring()
      this.tokenExpiresAt = null
      this.retryCount = 0
      this.nextCheckTime = null
      this.lastRenewalAttemptTime = null
      this._configInitializedFor = null
      Object.keys(this.listeners).forEach(key => {
         this.listeners[key] = []
      })
      console.log('✅ Session renewal service destroyed')
   }
}

// Lazy singleton to avoid initialization order issues with chunks
let instanceCache = null

function getInstance() {
   if (!instanceCache) {
      instanceCache = new SessionRenewalService()
   }
   return instanceCache
}

// Export singleton getter with method binding to preserve 'this' context
export const sessionRenewalService = new Proxy({}, {
    get(target, prop) {
       const instance = getInstance()
       const value = instance[prop]
       // Bind methods to preserve 'this' context when called through Proxy
       if (typeof value === 'function') {
          return value.bind(instance)
       }
       return value
    },
    set(target, prop, value) {
       const instance = getInstance()
       instance[prop] = value
       return true
    }
})
